import { useState } from "react";
import { createUserWithEmailAndPassword, sendEmailVerification } from "firebase/auth";
import { doc, setDoc, serverTimestamp, collection, query, where, getDocs } from "firebase/firestore";
import { auth, db } from "../firebase";
import { useNavigate, Link } from "react-router-dom";

export default function Signup() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [marketing, setMarketing] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkingName, setCheckingName] = useState(false);
  const navigate = useNavigate();

  async function checkDisplayNameUnique(name) {
    try {
      const q = query(
        collection(db, "users"),
        where("displayNameLower", "==", name.trim().toLowerCase())
      );
      const snap = await getDocs(q);
      return snap.empty;
    } catch (err) {
      // If the check fails (e.g. Firestore rules), allow signup to proceed
      console.warn("Could not check display name uniqueness:", err.message);
      return true;
    }
  }

  async function handleSignup(e) {
    e.preventDefault();
    setError("");

    if (!firstName.trim() || !lastName.trim()) return setError("Please enter your first and last name.");
    if (!displayName.trim()) return setError("Please choose a display name.");
    if (password.length < 6) return setError("Password must be at least 6 characters.");
    if (!agreeTerms) return setError("You must agree to the terms and conditions to continue.");

    setLoading(true);
    setCheckingName(true);

    const isUnique = await checkDisplayNameUnique(displayName);

    setCheckingName(false);

    if (!isUnique) {
      setError("That display name is already taken. Please choose another.");
      setLoading(false);
      return;
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Send verification email — don't block signup if this fails
      try {
  await sendEmailVerification(user);
} catch (verifyErr) {
  console.warn("Could not send verification email:", verifyErr.message);
}

      await setDoc(doc(db, "users", user.uid), {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: user.email,
        displayName: displayName.trim(),
        displayNameLower: displayName.trim().toLowerCase(),
        isPremium: false,
        emailNotifications,
        marketing,
        createdAt: serverTimestamp(),
      });

      navigate("/verify-email");
    } catch (err) {
      if (err.code === "auth/email-already-in-use") {
        setError("An account with this email already exists.");
      } else if (err.code === "auth/invalid-email") {
        setError("Please enter a valid email address.");
      } else if (err.code === "auth/weak-password") {
        setError("Password must be at least 6 characters.");
      } else {
        setError("Failed to create account. " + err.message);
      }
    }

    setLoading(false);
  }

  const Checkbox = ({ checked, onChange, label, sublabel }) => (
    <label className="flex items-start gap-3 cursor-pointer group">
      <div
        onClick={() => onChange(!checked)}
        className="mt-0.5 w-5 h-5 rounded flex-shrink-0 flex items-center justify-center transition-all"
        style={{
          backgroundColor: checked ? "#C8FF00" : "#F5F6F2",
          border: checked ? "2px solid #C8FF00" : "2px solid #E0E2DB",
        }}
      >
        {checked && <span style={{ color: "#0A0B0F", fontSize: "12px", fontWeight: "bold" }}>✓</span>}
      </div>
      <div>
        <span className="font-condensed text-base tracking-wide" style={{ color: "#C8FF00" }}>{label}</span>
        {sublabel && <p className="font-condensed text-gray-400 text-sm tracking-wide mt-0.5">{sublabel}</p>}
      </div>
    </label>
  );

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: "#0A0B0F" }}>

      {/* Left panel */}
      <div
        className="hidden lg:flex flex-col justify-between w-2/5 p-12 diagonal-cut"
        style={{ backgroundColor: "#FF5C00" }}
      >
        <div className="font-display text-4xl text-white tracking-wider">PREDICTORZ.</div>
        <div>
          <div className="font-display text-8xl text-white leading-none mb-6">
            JOIN THE<br />ACTION.
          </div>
          <div className="space-y-4">
            {[
              "Predict match outcomes",
              "Compete in private leagues",
              "Earn points every matchday",
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-white/60" />
                <span className="font-condensed text-xl text-white/80 tracking-wide">{item}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="font-condensed text-white/40 tracking-wide mb-16">
          Free to join · No credit card required
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center px-8 py-12">
        <div className="w-full max-w-lg fade-up">
          <h1 className="font-display text-6xl text-white tracking-wider mb-2">CREATE ACCOUNT</h1>
          <p className="font-condensed text-gray-400 text-xl tracking-wide mb-8">Free to join, forever</p>

          {error && (
            <div className="mb-6 px-4 py-3 rounded-lg font-condensed tracking-wide text-red-400"
              style={{ backgroundColor: "#1A0A0A", border: "1px solid #3A1A1A" }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSignup} className="space-y-5">

            {/* Name row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="font-condensed text-gray-400 tracking-widest text-sm uppercase block mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  required
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full rounded-xl px-4 py-3 text-lg"
                />
              </div>
              <div>
                <label className="font-condensed text-gray-400 tracking-widest text-sm uppercase block mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  required
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full rounded-xl px-4 py-3 text-lg"
                />
              </div>
            </div>

            {/* Display name */}
            <div>
              <label className="font-condensed text-gray-400 tracking-widest text-sm uppercase block mb-2">
                Display Name
              </label>
              <input
                type="text"
                required
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="w-full rounded-xl px-4 py-3 text-lg"
                placeholder="e.g. TopBanana99"
              />
              <p className="font-condensed text-sm mt-2 tracking-wide" style={{ color: "#FF5C00" }}>
                ⚠ This is permanent and cannot be changed after sign up. Choose carefully — it's how everyone will see you on leaderboards.
              </p>
            </div>

            {/* Email */}
            <div>
              <label className="font-condensed text-gray-400 tracking-widest text-sm uppercase block mb-2">
                Email Address
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-xl px-4 py-3 text-lg"
              />
            </div>

            {/* Password */}
            <div>
              <label className="font-condensed text-gray-400 tracking-widest text-sm uppercase block mb-2">
                Password
              </label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-xl px-4 py-3 text-lg"
              />
              <p className="font-condensed text-gray-500 text-sm mt-1 tracking-wide">Minimum 6 characters</p>
            </div>

            {/* Checkboxes */}
            <div className="rounded-xl p-5 space-y-4" style={{ backgroundColor: "#161618", border: "1px solid #2A2A2E" }}>
              <Checkbox
                checked={agreeTerms}
                onChange={setAgreeTerms}
                label="I agree to the Terms & Conditions"
                sublabel="Required to create an account"
              />
              <Checkbox
                checked={emailNotifications}
                onChange={setEmailNotifications}
                label="Notify me about upcoming predictions"
                sublabel="We'll email you before prediction deadlines"
              />
              <Checkbox
                checked={marketing}
                onChange={setMarketing}
                label="Receive marketing emails"
                sublabel="News, features, and offers from Predictorz"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-lime w-full py-4 rounded-xl text-xl mt-2"
            >
              {checkingName ? "CHECKING NAME..." : loading ? "CREATING ACCOUNT..." : "CREATE ACCOUNT"}
            </button>
          </form>

          <p className="font-condensed text-gray-500 text-lg mt-8 text-center tracking-wide">
            Already have an account?{" "}
            <Link to="/login" className="hover:underline" style={{ color: "#C8FF00" }}>Log in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
